import { CarrotPlanMediaItem, DisplayStatus } from "../../../types/CarrotPlanOptions"
import { buildMediaLink, buildSubCategoryLink } from "./contentfulLinkBuilder"
import {
  CarrotPlanFormOption,
  MediaOptionFields,
  MenuItemOptionFields,
  TopicOptionFields
} from "../hooks/useCarrotPlanMappings"
import { CarrotPlanMediaStepVals, GetCarrotPlanMediaTypeIdForStepVal } from "../MediaTypes"

// Helper: filters out media items that should be hidden OR that have the incorrect media type
export function getFilteredMediaItems(mediaItems: CarrotPlanMediaItem[], mediaTypeId: string) {
  const mediaTypeIsCorrect = (mediaItem: CarrotPlanMediaItem): boolean => mediaItem.contentType === mediaTypeId
  const contentShouldBeHidden = (mediaItem: CarrotPlanMediaItem): boolean =>
    [DisplayStatus.ARCHIVED.toString(), DisplayStatus.REMOVE.toString(), DisplayStatus.UNASSIGNED.toString()].includes(
      mediaItem.optionMetadata.displayStatus
    )
  return mediaItems.filter((mediaItem) => mediaTypeIsCorrect(mediaItem) && !contentShouldBeHidden(mediaItem))
}

// Helper: filters to only include recommended media items with the correct ("SHOW") display status
export function getOnlyRecommendedMediaItems(mediaItems: CarrotPlanMediaItem[]) {
  const isRecommended = (mediaItem: CarrotPlanMediaItem): boolean => mediaItem.optionMetadata.isRecommended
  const displayStatusIsShow = (mediaItem: CarrotPlanMediaItem): boolean =>
    mediaItem.optionMetadata.displayStatus === "SHOW"
  return mediaItems.filter((mediaItem) => isRecommended(mediaItem) && displayStatusIsShow(mediaItem))
}

// Helper: filters menu item options to the journey-required ones that are not DISABLED.
export function getOnlyRequiredMenuItems(
  menuItems: CarrotPlanFormOption<MenuItemOptionFields>[] | null | undefined
): CarrotPlanFormOption<MenuItemOptionFields>[] {
  return (menuItems ?? []).filter(
    (item) => item.optionMetadata?.isRequired === true && item.optionMetadata?.displayStatus !== DisplayStatus.DISABLE
  )
}

// Transforms a media item into the shape expected by the autocomplete form
export function getAutocompleteMediaItemOption(
  mediaItem: CarrotPlanMediaItem
): CarrotPlanFormOption<MediaOptionFields> {
  const mediaLink = buildMediaLink({
    fields: { label: mediaItem.title, slug: mediaItem.slug },
    categoryTitle: mediaItem.categoryTitle,
    mediaTypeId: mediaItem.contentType
  })
  return {
    label: mediaItem.title,
    value: mediaItem.entryId,
    optionMetadata: mediaItem.optionMetadata,
    optionFields: {
      link: mediaLink,
      entryId: mediaItem.entryId,
      slug: mediaItem.slug,
      categoryTitle: mediaItem.categoryTitle,
      contentType: mediaItem.contentType
    }
  }
}

// Transforms all given media items to the shape expected by the autocomplete
export function getAutocompleteMediaItemOptions(
  mediaItems: CarrotPlanMediaItem[]
): CarrotPlanFormOption<MediaOptionFields>[] {
  const mappedMediaOptions: CarrotPlanFormOption<MediaOptionFields>[] = []
  mediaItems?.forEach((mediaItem) => mappedMediaOptions.push(getAutocompleteMediaItemOption(mediaItem)))
  return mappedMediaOptions
}

// Transforms a topic option into the shape expected by the autocomplete form
export function getAutocompleteTopicOption(
  menuItemLabel: string,
  topicOption: CarrotPlanFormOption<TopicOptionFields>,
  isRecommendedTopic = false
): CarrotPlanFormOption<TopicOptionFields> {
  const topicLink = buildSubCategoryLink({
    fields: { label: topicOption.label, navTitle: topicOption.optionFields.navTitle },
    categoryTitle: topicOption.group
  })
  const mediaTypeId = GetCarrotPlanMediaTypeIdForStepVal(menuItemLabel).toString()
  const filteredMediaItems = getFilteredMediaItems(topicOption.optionFields.options, mediaTypeId)
  const mediaItemOptions = getAutocompleteMediaItemOptions(filteredMediaItems)
  const hasRecommendedMedia: boolean = mediaItemOptions.some(
    (item: CarrotPlanFormOption<MediaOptionFields>) =>
      item.optionMetadata.isRecommended && item.optionMetadata?.displayStatus === "SHOW"
  )

  return {
    label: isRecommendedTopic ? topicOption.optionFields.titleWithCategory : topicOption.label,
    group: topicOption.group,
    value: topicOption.value,
    optionMetadata: hasRecommendedMedia
      ? { isRecommended: true, ...topicOption.optionMetadata }
      : topicOption.optionMetadata,
    optionFields: {
      entityId: topicOption.optionFields.entityId,
      link: topicLink,
      navTitle: topicOption.optionFields.navTitle,
      titleWithCategory: isRecommendedTopic ? topicOption.optionFields.titleWithCategory : null,
      options: filteredMediaItems
    }
  }
}

// Transforms ALL topic options into the shape expected by the autocomplete form (nested media items are NOT updated to media options)
// Also adds a disabled message to the options that are already added to the same plan and filters out any topics without media items
export function getAutocompleteTopicOptions(
  menuItemLabel: string,
  topicOptions: CarrotPlanFormOption<TopicOptionFields>[]
): CarrotPlanFormOption<TopicOptionFields>[] {
  if (topicOptions && CarrotPlanMediaStepVals.includes(menuItemLabel)) {
    const topicOptionsForAutocomplete: CarrotPlanFormOption<TopicOptionFields>[] = topicOptions.map((topicOption) => {
      const mediaTypeId = GetCarrotPlanMediaTypeIdForStepVal(menuItemLabel)
      const mediaOptions = topicOption.optionFields.options
      const filteredMediaOptions = getFilteredMediaItems(mediaOptions, mediaTypeId)
      const mediaItemOptions = getAutocompleteMediaItemOptions(filteredMediaOptions)
      const hasRecommendedMedia: boolean = mediaItemOptions.some(
        (item: CarrotPlanFormOption<MediaOptionFields>) =>
          item.optionMetadata.isRecommended && item.optionMetadata?.displayStatus === "SHOW"
      )

      // If topic has recommended media, transform it to show recommended items and mark as recommended
      if (hasRecommendedMedia) {
        return getAutocompleteTopicOption(menuItemLabel, topicOption, true)
      }

      return getAutocompleteTopicOption(menuItemLabel, topicOption)
    })

    // filter out topics that don't have any options
    return topicOptionsForAutocomplete.filter((topic) => topic?.optionFields?.options.length > 0)
  }
  return []
}
